import type { ReactNode, Ref } from 'react';
import { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import ErrorOutline from '@mui/icons-material/ErrorOutlineRounded';
import Refresh from '@mui/icons-material/Refresh';
import ContentCopy from '@mui/icons-material/ContentCopy';
import Check from '@mui/icons-material/Check';
import { alpha } from '@mui/material/styles';

export interface ErrorStateProps {
  /** Friendly headline (translated copy). */
  title: string;
  /** Optional supporting body (translated copy). */
  message?: ReactNode;
  /**
   * RFC-9457 trace id from the problem response. Rendered monospace with a copy
   * button so support can quote it.
   */
  traceId?: string;
  /** Retry handler; the "Try again" button only shows when this is set. */
  onRetry?: () => void;
  /** Label for the retry button (translated copy; required to show it). */
  retryLabel?: string;
  /** Tighter padding for inline/table usage. */
  compact?: boolean;
  ref?: Ref<HTMLDivElement>;
}

/**
 * Failure panel. Surfaces the `traceId` (always present on API problem
 * responses) in a copyable monospace chip and offers an optional retry.
 */
export function ErrorState({
  title,
  message,
  traceId,
  onRetry,
  retryLabel,
  compact = false,
  ref,
}: ErrorStateProps) {
  const [copied, setCopied] = useState(false);

  const copyTraceId = () => {
    if (!traceId) return;
    navigator.clipboard?.writeText(traceId);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    <Box
      ref={ref}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: 1.25,
        p: compact ? '32px 24px' : '52px 32px',
        maxWidth: 460,
        mx: 'auto',
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          bgcolor: (theme) => alpha(theme.palette.error.main, 0.1),
          border: (theme) => `1px solid ${alpha(theme.palette.error.main, 0.28)}`,
        }}
      >
        <ErrorOutline sx={{ fontSize: 26, color: 'error.main' }} />
      </Box>
      <Typography component="div" sx={{ fontSize: 16, fontWeight: 600, color: 'text.primary' }}>
        {title}
      </Typography>
      {message && (
        <Typography component="div" sx={{ fontSize: 14, color: 'text.secondary' }}>
          {message}
        </Typography>
      )}
      {traceId && (
        <Box
          component="button"
          type="button"
          onClick={copyTraceId}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            p: '5px 10px',
            bgcolor: 'background.default',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '6px',
            cursor: 'pointer',
            fontFamily: 'var(--font-mono, ui-monospace, monospace)',
            fontSize: 12,
            fontWeight: 500,
            color: 'text.secondary',
          }}
        >
          <Box component="span" sx={{ color: 'text.secondary' }}>
            traceId
          </Box>
          <Box component="span" sx={{ color: 'text.primary' }}>
            {traceId}
          </Box>
          {copied ? (
            <Check sx={{ fontSize: 13, color: 'success.main' }} />
          ) : (
            <ContentCopy sx={{ fontSize: 13 }} />
          )}
        </Box>
      )}
      {onRetry && retryLabel && (
        <Box sx={{ mt: 0.75 }}>
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            startIcon={<Refresh />}
            onClick={onRetry}
          >
            {retryLabel}
          </Button>
        </Box>
      )}
    </Box>
  );
}
